import React, { useEffect, useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import MaterialTable from "material-table";
import { Paper, Grid, Button, Snackbar, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import { blue, green, grey, red } from "@material-ui/core/colors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faSave,
  faList,
  faClipboardList,
  faUsersCog,
  faAngleRight,
  faAngleLeft,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faSearch,
  faSortUp,
  faTimes,
  faFilter,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";

import { Link } from "../../Link";
import { _url } from "../../../helpers/makeUrl";
import { MaterialTableLocaleDelayed } from "../../../i18n/material-table";
import { _t } from "../../../i18n/lang";
import AjaxLoading from "../../AjaxLoading";
import AssignedCustomers from "./AssignedCustomers";

import { ROUTES_BY_ID as RouteDictionary } from "../../../constants/routes";
import { modernReportingService } from "../../../services/modernReportingService";

const tableIcons = {
  NextPage: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faAngleRight} {...props} />
  )),
  PreviousPage: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faAngleLeft} {...props} />
  )),
  FirstPage: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faAngleDoubleLeft} {...props} />
  )),
  LastPage: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faAngleDoubleRight} {...props} />
  )),
  Search: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faSearch} {...props} />
  )),
  SortArrow: React.forwardRef((props, ref) => (
    <FontAwesomeIcon {...props} icon={faSortUp} />
  )),
  ResetSearch: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faTimes} {...props} size="xs" />
  )),
  Filter: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faFilter} {...props} />
  )),
};

const useStyles = makeStyles((theme) => ({
  wrap: {
    flexGrow: 1,
    paddingRight: "1em",
    paddingLeft: "1em",
  },
  paper: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  headerWrap: {
    borderBottom: "1px solid #ccc",
    paddingBottom: "20px",
    marginBottom: "32px",
  },
  headerIcon: {
    marginRight: "4px",
  },
  floatRight: {
    float: "right",
  },
  textRight: {
    textAlign: "right",
  },
  fullWidth: {
    width: "100%",
  },
  textRed: {
    color: "red",
  },
  fieldLabel: {
    marginTop: "6px",
  },
}));

const statusColor = (status) => {
  if (status === "DELETED") {
    return red[500];
  }

  if (status === "ACTIVE") {
    return green[500];
  }
  return "blueGrey";
};

const OrderDetails = (props) => {
  const [orderDetails, setOrderDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    type: "success",
    duration: 6000,
  });
  const [partnerCustomers, setPartnerCustomers] = useState([]);
  const tableRef = useRef();

  const orderId = props.match.params.id;

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const { details } =
        await modernReportingService.currentOrderDetails(orderId);
      setOrderDetails(details);
      setLoading(false);
    }

    async function fetchPartnerCustomers() {
      const { customers } = await modernReportingService.customers();
      setPartnerCustomers(customers);
    }

    fetchData();
    fetchPartnerCustomers();
  }, []);

  const showAlert = (type, message, duration = 6000) => {
    setAlert({
      open: true,
      message,
      type,
      duration,
    });
  };

  const closeAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlert({
      open: false,
      message: "",
      type: "success",
    });
  };

  const classes = useStyles();

  const styleCss = {
    fontSize: "10px",
  };

  const columns = [
    {
      title: _t("MMR_CURRENT_ORDER_DET_TABLE_HEAD_PART"),
      field: "part",
      cellStyle: { textAlign: "center" },
      headerStyle: { textAlign: "center" },
    },
    {
      title: _t("MMR_CURRENT_ORDER_DET_TABLE_HEAD_VEND_PART"),
      field: "vendorPart",
      cellStyle: { textAlign: "center" },
      headerStyle: {
        textAlign: "center",
      },
    },

    {
      title: _t("MMR_CURRENT_ORDER_DET_TABLE_HEAD_PART_NAME"),
      field: "partType",
    },
    {
      title: _t("MMR_CURRENT_ORDER_DET_TABLE_HEAD_PART_TYPE"),
      field: "partName",
      render(rowData) {
        const name = rowData.partName.replace("%", "");
        return <label style={styleCss}>{name}</label>;
      },
    },
    {
      title: _t("MMR_CURRENT_ORDER_DET_TABLE_HEAD_PRICE"),
      field: "price",
      render(rowData) {
        const currency = rowData.currency.toUpperCase();
        return (
          <label>
            {rowData.price} {currency}
          </label>
        );
      },
    },
    { title: _t("MMR_CURRENT_ORDER_DET_TABLE_HEAD_QTY"), field: "qtyOrdered" },
    {
      title: _t("MMR_CURRENT_ORDER_DET_TABLE_HEAD_QTY_TOTAL"),
      field: "qtyOrderTotal",
    },
  ];

  const alertBorder = (type) => {
    let alertBorderColor = "#28a745";

    if (type === "info") {
      alertBorderColor = "#2196f3";
    }

    if (alert.type === "warning") {
      alertBorderColor = "#ff9800";
    }

    if (alert.type === "error") {
      alertBorderColor = "#8a1507";
    }

    return alertBorderColor;
  };

  const customDetailPanel = (row) => (
    <AssignedCustomers detail={row} customers={partnerCustomers} />
  );

  const alertBorderColor = alertBorder(alert.type);
  return (
    <div className={classes.wrap}>
      <Snackbar
        open={alert.open}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={6000}
        onClose={closeAlert}
      >
        {alert.open ? (
          <Alert
            onClose={closeAlert}
            severity={alert.type}
            style={{ border: `1px solid ${alertBorderColor}` }}
          >
            <div dangerouslySetInnerHTML={{ __html: alert.message }} />
          </Alert>
        ) : (
          <span></span>
        )}
      </Snackbar>
      <Grid container spacing={3} direction="row">
        <Grid item xs={1}></Grid>
        <Grid item xs={10}>
          <Paper className={classes.paper}>
            <Typography
              variant="h5"
              gutterBottom
              className={classes.headerWrap}
            >
              <FontAwesomeIcon
                icon={faClipboardList}
                className={classes.headerIcon}
              />
              {_t("MMR_ORDER_DETAILS")}
              <Button
                variant="contained"
                component={Link}
                to={RouteDictionary.current_orders.path}
                size="small"
                className={classes.floatRight}
              >
                <FontAwesomeIcon icon={faList} className={classes.headerIcon} />
                {_t("MMR_RETURN_TO_LIST")}
              </Button>
            </Typography>
            {loading ? (
              <div style={{ position: "relative", minHeight: "10em" }}>
                <AjaxLoading />
              </div>
            ) : (
              <div>
                <div style={{ padding: "0em 0em 5em 0em" }}>
                  <MaterialTable
                    tableRef={tableRef}
                    columns={columns}
                    data={
                      orderDetails?.map((data) => ({
                        ...data,
                        tableData: {
                          showDetailPanel: customDetailPanel,
                        },
                      })) || []
                    }
                    localization={MaterialTableLocaleDelayed()}
                    options={{
                      showTitle: false,
                      toolbar: false,
                      search: false,
                      paging: false,
                      actionsColumnIndex: -1,
                      detailPanelColumnAlignment: "right",
                      pageSizeOptions: [5, 10, 20, 50, 100],
                      headerStyle: {
                        backgroundColor: grey[300],
                      },
                    }}
                    icons={tableIcons}
                  />
                </div>
              </div>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default OrderDetails;

